import { Confirmation, Summary } from './components';
import { ModifyProps, ModifyStages } from './modify.types';
import React, { useCallback, useMemo } from 'react';

import { ModalIds } from '../../../../../../contexts/modals/modals.types';
import { useModalsContext } from '../../../../../../contexts';

/**
 * useModifyData hook
 * @description The hook which processes Order Modify flow data
 *
 * @author Oleksii Medvediev
 * @category Hooks
 */
const useModifyData = () => {
  const {
    data: { orderModify },
    dispatch,
  } = useModalsContext();

  const flowData: ModifyProps | undefined = useMemo(() => orderModify, [orderModify]);

  const setStage = useCallback(
    (stage: ModifyProps['currentStage']) =>
      flowData &&
      dispatch({
        type: 'UPDATE_MODAL_DATA',
        payload: {
          id: ModalIds.orderModify,
          data: {
            [ModalIds.orderModify]: { ...flowData, currentStage: stage },
          },
        },
      }),
    [dispatch, flowData],
  );

  const submitHandlers: Record<ModifyProps['currentStage'], () => void> = useMemo(
    () => ({
      [ModifyStages.summary]: () => {
        setStage(ModifyStages.confirmation);
      },
      [ModifyStages.confirmation]: async () => {
        flowData?.onSubmitModify && flowData.onSubmitModify();
      },
    }),
    [flowData, setStage],
  );

  const stages: Record<ModifyProps['currentStage'], JSX.Element> | undefined = useMemo(
    () =>
      flowData
        ? {
            [ModifyStages.summary]: (
              <Summary
                onClose={() => dispatch({ type: 'HIDE_MODAL', payload: ModalIds.orderModify })}
                onSubmit={submitHandlers.summary}
                summaryRecommendations={flowData.recommendations.map(
                  ({ account, cardDescription, id, order, orderDescription, symbol, orderDescriptionOld, action }) => ({
                    account,
                    cardDescription,
                    id,
                    order,
                    orderDescription,
                    symbol,
                    orderDescriptionOld,
                    action,
                  }),
                )}
              />
            ),
            [ModifyStages.confirmation]: (
              <Confirmation
                onClose={() => {
                  dispatch({ type: 'HIDE_MODAL', payload: ModalIds.orderModify });
                }}
                onSubmit={submitHandlers.confirmation}
              />
            ),
          }
        : undefined,
    [dispatch, flowData, submitHandlers.confirmation, submitHandlers.summary],
  );

  return {
    stages,
    currentStage: useMemo(() => flowData?.currentStage, [flowData?.currentStage]),
  };
};

export { useModifyData };
